import type { Deck } from "@/types/linguadeck";
import type { DeckActivitySignal } from "@/lib/db";

export type FolderSortOption = "newest" | "oldest" | "alphabetical" | "recent" | "cards";

export interface FolderWorkspacePrefs {
  sort: FolderSortOption;
  pinnedDeckIds: string[];
}

const DEFAULT_PREFS: FolderWorkspacePrefs = {
  sort: "newest",
  pinnedDeckIds: [],
};

function getWorkspaceKey(userId: string | null, folderId: string): string {
  return `studydo_folder_workspace_${userId || "anon"}_${folderId}`;
}

export function loadFolderWorkspacePrefs(userId: string | null, folderId: string): FolderWorkspacePrefs {
  if (!folderId || typeof localStorage === "undefined") return { ...DEFAULT_PREFS };
  try {
    const raw = localStorage.getItem(getWorkspaceKey(userId, folderId));
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<FolderWorkspacePrefs>;
    const sort = parsed.sort;
    return {
      sort: sort === "oldest" || sort === "alphabetical" || sort === "recent" || sort === "cards" ? sort : "newest",
      pinnedDeckIds: Array.isArray(parsed.pinnedDeckIds)
        ? parsed.pinnedDeckIds.filter((id): id is string => typeof id === "string")
        : [],
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveFolderWorkspacePrefs(userId: string | null, folderId: string, prefs: FolderWorkspacePrefs): void {
  if (!folderId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(getWorkspaceKey(userId, folderId), JSON.stringify(prefs));
  } catch {
    // Ignore storage failures.
  }
}

function compareAlphabetical(a: Deck, b: Deck): number {
  return a.title.localeCompare(b.title);
}

function compareByCreatedAt(a: Deck, b: Deck, ascending: boolean): number {
  const aTime = a.created_at ? Date.parse(a.created_at) : 0;
  const bTime = b.created_at ? Date.parse(b.created_at) : 0;
  if (aTime !== bTime) {
    return ascending ? aTime - bTime : bTime - aTime;
  }
  return compareAlphabetical(a, b);
}

function compareByRecentActivity(
  a: Deck,
  b: Deck,
  signals: Record<string, DeckActivitySignal>
): number {
  const aTime = signals[a.id]?.lastStudiedAt ? Date.parse(signals[a.id].lastStudiedAt as string) : 0;
  const bTime = signals[b.id]?.lastStudiedAt ? Date.parse(signals[b.id].lastStudiedAt as string) : 0;
  if (aTime !== bTime) return bTime - aTime;
  return compareByCreatedAt(a, b, false);
}

function compareByCards(a: Deck, b: Deck): number {
  const aCount = a.cards?.length ?? 0;
  const bCount = b.cards?.length ?? 0;
  if (aCount !== bCount) return bCount - aCount;
  return compareByCreatedAt(a, b, false);
}

export function sortFolderDecks(
  decks: Deck[],
  sort: FolderSortOption,
  pinnedDeckIds: string[],
  signals: Record<string, DeckActivitySignal>
): Deck[] {
  const pinnedSet = new Set(pinnedDeckIds);
  const comparator = (a: Deck, b: Deck) => {
    switch (sort) {
      case "oldest":
        return compareByCreatedAt(a, b, true);
      case "alphabetical":
        return compareAlphabetical(a, b);
      case "recent":
        return compareByRecentActivity(a, b, signals);
      case "cards":
        return compareByCards(a, b);
      case "newest":
      default:
        return compareByCreatedAt(a, b, false);
    }
  };

  return [...decks].sort((a, b) => {
    const aPinned = pinnedSet.has(a.id);
    const bPinned = pinnedSet.has(b.id);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    return comparator(a, b);
  });
}
